import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { BarChart3, Heart, ThumbsDown, ShoppingBag, DollarSign, Music, Search } from "lucide-react";
import PayoutPanel from "@/components/PayoutPanel";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (user?.role !== "artist") return;
    (async () => {
      const { data } = await api.get("/dashboard");
      setData(data);
    })();
  }, [user]);

  const filtered = useMemo(
    () => (data ? data.items.filter((i) => i.title.toLowerCase().includes(q.toLowerCase())) : []),
    [data, q]
  );

  if (user && user.role !== "artist") return <Navigate to="/" replace />;
  if (!data) return <div className="text-center text-zinc-500 py-12">{t.common.loading}</div>;

  const stats = [
    { label: t.dashboard.tracks, value: data.totals.tracks, icon: Music, color: "from-[#FF0050] to-[#8A2BE2]" },
    { label: t.dashboard.sales, value: data.totals.sales, icon: ShoppingBag, color: "from-emerald-500 to-teal-700" },
    { label: t.dashboard.revenue, value: `$${data.totals.revenue.toFixed(2)}`, icon: DollarSign, color: "from-yellow-400 to-orange-600" },
    { label: t.dashboard.likes, value: data.totals.likes, icon: Heart, color: "from-pink-500 to-rose-700" },
    { label: t.dashboard.dislikes, value: data.totals.dislikes, icon: ThumbsDown, color: "from-zinc-500 to-zinc-700" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <header className="anim-in mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF0050] font-bold flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> {t.dashboard.title}
          </p>
          <h1 className="text-3xl sm:text-4xl font-black font-display text-white">{t.dashboard.title}</h1>
          <p className="text-zinc-400 text-sm mt-1">{user.name}</p>
        </div>
        <Link to={`/profile/${user.id}`} className="btn-secondary text-sm">{t.dashboard.backToProfile}</Link>
      </header>

      <PayoutPanel />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mb-6">
        {stats.map((s, i) => (
          <div key={s.label} className={`card-flat p-4 anim-in`} style={{ animationDelay: `${i * 0.05}s` }} data-testid={`stat-${s.label}`}>
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s.color} grid place-items-center text-white mb-3`}>
              <s.icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-black font-display text-white">{s.value}</p>
            <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="relative mb-4 anim-in">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.dashboard.searchPh}
          className="input-base pl-10"
          data-testid="dashboard-search"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-zinc-500 py-12">{t.dashboard.noTracks}</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block card-flat overflow-hidden" data-testid="dashboard-table">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase tracking-wider bg-white/[0.02]">
                <tr>
                  <th className="text-left p-3">{t.dashboard.cover}</th>
                  <th className="text-left p-3">{t.dashboard.col_title}</th>
                  <th className="text-left p-3">{t.dashboard.col_type}</th>
                  <th className="text-right p-3">{t.dashboard.col_sales}</th>
                  <th className="text-right p-3">{t.dashboard.col_revenue}</th>
                  <th className="text-right p-3">{t.dashboard.col_likes}</th>
                  <th className="text-right p-3">{t.dashboard.col_dislikes}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`dash-row-${i.id}`}>
                    <td className="p-3"><img src={resolveImg(i.cover)} alt="" className="w-10 h-10 rounded object-cover" /></td>
                    <td className="p-3 text-white font-medium">{i.title}</td>
                    <td className="p-3"><span className="chip bg-white/5 text-zinc-300 border border-white/5 capitalize">{i.release_type}</span></td>
                    <td className="p-3 text-right text-white tabular-nums">{i.purchases}</td>
                    <td className="p-3 text-right text-white tabular-nums">${i.revenue.toFixed(2)}</td>
                    <td className="p-3 text-right text-white tabular-nums">{i.likes}</td>
                    <td className="p-3 text-right text-white tabular-nums">{i.dislikes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2" data-testid="dashboard-list-mobile">
            {filtered.map((i) => (
              <div key={i.id} className="card-flat p-3 flex items-center gap-3" data-testid={`dash-mob-${i.id}`}>
                <img src={resolveImg(i.cover)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{i.title}</p>
                  <p className="text-xs text-zinc-500 capitalize">{i.release_type}</p>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-xs">
                    <span className="text-emerald-300">{i.purchases} {t.dashboard.salesShort}</span>
                    <span className="text-yellow-300">${i.revenue.toFixed(2)}</span>
                    <span className="text-pink-300">{i.likes} <Heart className="w-3 h-3 inline" /></span>
                    <span className="text-zinc-400">{i.dislikes} <ThumbsDown className="w-3 h-3 inline" /></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
